import { useState, useEffect, useRef, useCallback } from "react";
import { C, S } from "../customers/theme";
import EmptyState from "../components/EmptyState";
import { sendAssistantMessage } from "../lib/aiAssistant";

// AtlasChatPanel.jsx (P4-CC12) — right-side drawer on desktop, bottom sheet on
// mobile. Conversation held in memory for this session only (plain useState,
// no persistence) — matches the WP's explicit scope (no DB persistence of
// message content in this work package).

// Mirrors useIsMobile(breakpoint) in src/MsquaredApp.jsx exactly — duplicated
// locally rather than imported, to avoid a circular import between the
// mounting parent (MsquaredApp -> AtlasAssistant -> AtlasChatPanel) and this
// mounted child.
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

const STARTERS = [
  "How do I create a new invoice?",
  "How do I scan a receipt to log an expense?",
  "How do I add a new customer?",
  "How do I issue a credit note?",
];

const Z_PANEL = 55;
let _id = 0;
const nextId = () => `atlas-msg-${++_id}`;

export default function AtlasChatPanel({ onClose }) {
  const isMobile = useIsMobile();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [error, setError] = useState(null); // { message, lastUserMessage } | null
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, []);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages, typing]);

  // Focus trap + Escape-to-close (Escape also returns focus to the avatar, via
  // the onClose callback supplied by AtlasAssistant).
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key !== "Tab" || !panelRef.current) return;
      const focusable = panelRef.current.querySelectorAll(
        'button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0], last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const send = useCallback(async (text) => {
    const trimmed = String(text || "").trim();
    if (!trimmed || typing) return;
    setError(null);
    const history = messages.map(({ role, content }) => ({ role, content }));
    setMessages((m) => [...m, { id: nextId(), role: "user", content: trimmed }]);
    setInput("");
    setTyping(true);
    try {
      const reply = await sendAssistantMessage(trimmed, history);
      setMessages((m) => [...m, { id: nextId(), role: "assistant", content: reply }]);
    } catch (e) {
      setError({ message: e.message || "Could not reach Atlas.", lastUserMessage: trimmed });
    } finally {
      setTyping(false);
    }
  }, [messages, typing]);

  const onSubmit = (e) => { e.preventDefault(); send(input); };
  const retry = () => { if (error) send(error.lastUserMessage); };

  const panelStyle = isMobile
    ? { position: "fixed", left: 0, right: 0, bottom: 0, maxHeight: "70vh", borderRadius: "16px 16px 0 0" }
    : { position: "fixed", top: 0, right: 0, bottom: 0, width: 360, maxWidth: "92vw" };

  return (
    <div
      ref={panelRef}
      role="dialog" aria-modal="true"
      // P4-CC23-B(2b.1) — the aria-label is what a screen-reader user hears at
      // FIRST CONTACT with this dialog, before any child content (including the
      // header sub-line below) is reached via browse-mode navigation. A full stop
      // is used as the sentence break, not an em dash — em dash announcement is
      // inconsistent across screen readers (some pause, some ignore it, some say
      // "dash"); a full stop is read as a reliable sentence boundary everywhere.
      aria-label="Atlas assistant chat. AI assistant by Msquared Automation Limited."
      style={{
        ...panelStyle, zIndex: Z_PANEL, background: C.surface, color: C.text, border: `1px solid ${C.border}`,
        boxShadow: "0 12px 40px rgba(0,0,0,0.45)", display: "flex", flexDirection: "column",
        paddingBottom: isMobile ? "env(safe-area-inset-bottom, 0px)" : 0,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Atlas</div>
          {/* EU AI Act Art 50(1) disclosure (P4-CC23-B) — persistent, in the FIXED
              header, never inside the scrollable message list, never dismissible.
              This is the "first contact" surface for anyone who opens the panel
              without ever seeing the greeting bubble (sessionStorage unavailable —
              see Effect A's catch{return} in AtlasAssistant.jsx). Exact required
              string; do not reword. */}
          <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>AI assistant by Msquared Automation Limited</div>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", color: C.textMuted, fontSize: 18, cursor: "pointer" }}>✕</button>
      </div>

      <div ref={listRef} style={{ flex: 1, overflowY: "auto", padding: 16 }} aria-live="polite">
        {messages.length === 0 ? (
          <EmptyState
            icon="🤖"
            title="Ask Atlas anything about the app"
            // EU AI Act Art 50(1) disclosure (P4-CC23-B), appended to the existing
            // message — title is untouched. Exact required sentence; do not reword.
            message="Try one of these, or type your own question below. Atlas is an AI assistant provided by Msquared Automation Limited. Check anything important before you rely on it."
            action={
              <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "stretch" }}>
                {STARTERS.map((q) => (
                  <button key={q} type="button" onClick={() => send(q)} style={{ ...S.btn("secondary"), textAlign: "left" }}>{q}</button>
                ))}
              </div>
            }
          />
        ) : (
          messages.map((m) => (
            <div key={m.id} style={{ marginBottom: 10, display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth: "85%", padding: "8px 12px", borderRadius: 10, fontSize: 13, lineHeight: 1.4, background: m.role === "user" ? C.primaryDim : C.surface2, color: C.text }}>
                {m.content}
              </div>
            </div>
          ))
        )}
        {typing && <div style={{ color: C.textMuted, fontSize: 12, fontStyle: "italic" }}>Atlas is typing…</div>}
        {error && (
          <div role="alert" style={{ marginTop: 8, background: C.dangerDim, border: `1px solid ${C.danger}`, borderRadius: 8, padding: "8px 12px", fontSize: 12.5 }}>
            ⚠ {error.message}
            <button type="button" onClick={retry} style={{ ...S.btn("ghost"), padding: "2px 8px", marginLeft: 6 }}>Retry</button>
          </div>
        )}
      </div>

      <form onSubmit={onSubmit} style={{ display: "flex", gap: 8, padding: 12, borderTop: `1px solid ${C.border}` }}>
        <input
          ref={inputRef} style={{ ...S.input, flex: 1 }} value={input}
          onChange={(e) => setInput(e.target.value)} placeholder="Ask Atlas…" aria-label="Message Atlas"
        />
        <button type="submit" style={S.btn()} disabled={typing || !input.trim()}>Send</button>
      </form>
    </div>
  );
}
